import React, { useCallback, useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { Button } from "antd";
import { FaPlay } from "react-icons/fa";
import { PauseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useThemeContext } from "../../../context/ThemeContext";
import { useAudioPlayerContext } from "../../../context/AudioPlayerContext";
import { selectHasActiveCall } from "../../../redux/slices/sipSlice";
import "./index.scss";

interface AudioPlayerProps {
  audioUrl: string;
  isUserMessage?: boolean;
  /** Unique identifier for this player. Defaults to audioUrl. */
  messageId?: string;
  /** When true, show skeleton waveform — audio is still being processed/loaded */
  isProcessing?: boolean;
  /** When true, disable play button — upload still in progress */
  disabled?: boolean;
}

const PlayIcon = FaPlay as React.FC;

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  isUserMessage = false,
  messageId: propMessageId,
  isProcessing = false,
  disabled = false,
}) => {
  const messageId = propMessageId ?? audioUrl;

  const { theme } = useThemeContext();
  const hasActiveCall = useSelector(selectHasActiveCall);
  const { playingId, endedId, isPlaying: globalIsPlaying, play, pause, getAudio, getAudioForUrl } =
    useAudioPlayerContext();

  const isMyAudio = playingId === messageId;
  const isPlaying = isMyAudio && globalIsPlaying;
  const didEnd = endedId === messageId;

  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurfer = useRef<WaveSurfer | null>(null);
  const isMounted = useRef(true);
  const wasPlayingRef = useRef(false);
  const isSyncingRef = useRef(false);
  const getAudioForUrlRef = useRef(getAudioForUrl);
  const isMyAudioRef = useRef(isMyAudio);

  useEffect(() => { getAudioForUrlRef.current = getAudioForUrl; }, [getAudioForUrl]);
  useEffect(() => { isMyAudioRef.current = isMyAudio; }, [isMyAudio]);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [waveformReady, setWaveformReady] = useState(false);

  const progressColor = isUserMessage
    ? theme === "light" ? "#dbe3f8" : "#000"
    : theme === "light" ? "#000" : "#dbe3f8";

  // ── WaveSurfer: waveform rendering + seek interaction ─────────────────────
  useEffect(() => {
    if (!waveformRef.current || isProcessing) return;
    isMounted.current = true;
    setWaveformReady(false);

    const abortController = new AbortController();
    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#7a7a7a",
      progressColor,
      cursorColor: "transparent",
      barWidth: 2,
      barRadius: 2,
      height: 40,
      normalize: true,
      interact: !disabled,
      fetchParams: { signal: abortController.signal },
    });

    wavesurfer.current = ws;

    ws.load(audioUrl).catch((err) => {
      if (err instanceof Error && err.name === "AbortError") return;
      console.error("[AudioPlayer] load error:", err);
    });

    ws.on("ready", () => {
      if (!isMounted.current) return;
      setDuration(ws.getDuration());
      setCurrentTime(0);
      setWaveformReady(true);

      const audio = getAudioForUrlRef.current(audioUrl);
      if (audio && isMyAudioRef.current && audio.duration > 0) {
        const progress = audio.currentTime / audio.duration;
        isSyncingRef.current = true;
        try { ws.seekTo(Math.min(1, Math.max(0, progress))); } catch (_) {}
        isSyncingRef.current = false;
        setCurrentTime(audio.currentTime);
      }
    });

    ws.on("interaction", (newTime: number) => {
      if (disabled) return;
      const audio = getAudioForUrlRef.current(audioUrl);
      if (!audio) return;

      if (isFinite(newTime) && newTime >= 0) {
        audio.currentTime = newTime;
        if (isMounted.current) setCurrentTime(newTime);
      }
    });

    return () => {
      isMounted.current = false;
      abortController.abort();
      try { ws.destroy(); } catch (err: any) {
        if (err instanceof Error && err.name !== "AbortError") {
          console.error("[AudioPlayer] destroy error:", err);
        }
      }
      wavesurfer.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, progressColor, isProcessing, disabled]);

  // ── Sync waveform position from global HTMLAudioElement while playing ──────
  useEffect(() => {
    if (!isMyAudio) return;

    const audio = getAudio();
    if (!audio) return;

    const onTimeUpdate = () => {
      if (!isMounted.current || !wavesurfer.current) return;
      const dur = audio.duration;
      if (dur > 0 && isFinite(dur)) {
        const progress = audio.currentTime / dur;
        isSyncingRef.current = true;
        try {
          wavesurfer.current.seekTo(Math.min(1, Math.max(0, progress)));
        } catch (_) {}
        isSyncingRef.current = false;
        setCurrentTime(audio.currentTime);
        setDuration(dur);
      }
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    onTimeUpdate();

    return () => audio.removeEventListener("timeupdate", onTimeUpdate);
  }, [isMyAudio, getAudio]);

  // ── Reset waveform only when audio finishes naturally ─────────────────────
  useEffect(() => {
    if (!didEnd) return;
    if (wavesurfer.current) {
      isSyncingRef.current = true;
      try { wavesurfer.current.seekTo(0); } catch (_) {}
      isSyncingRef.current = false;
    }
    setCurrentTime(0);
  }, [didEnd]);

  // ── Pause audio during VoIP calls; restore after ──────────────────────────
  useEffect(() => {
    if (hasActiveCall) {
      if (isMyAudio && isPlaying) {
        wasPlayingRef.current = true;
        pause();
        toast.info("Audio paused — call in progress", { toastId: "call-audio-duck" });
      }
    } else {
      if (wasPlayingRef.current && isMyAudio) {
        wasPlayingRef.current = false;
        getAudio()?.play().catch(console.error);
      }
    }
  }, [hasActiveCall]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlayback = useCallback(() => {
    if (disabled || isProcessing) return;
    play(audioUrl, messageId);
  }, [play, audioUrl, messageId, disabled, isProcessing]);

  const displayTime = formatTime(
    isPlaying || currentTime > 0 ? currentTime : duration,
  );

  const showSkeleton = isProcessing || (!waveformReady && !!audioUrl);

  return (
    <div className={`audio-player-wrapper${disabled ? " audio-disabled" : ""}`} style={{ color: progressColor }}>
      <Button
        type="text"
        className="pause-play-btn"
        onClick={togglePlayback}
        disabled={disabled || isProcessing}
        icon={
          isProcessing ? (
            <LoadingOutlined spin />
          ) : isPlaying ? (
            <PauseOutlined />
          ) : (
            <PlayIcon />
          )
        }
      />
      <div className="audio-waveform-container">
        {showSkeleton && (
          <div className="waveform-skeleton">
            {Array.from({ length: 28 }, (_, i) => (
              <div
                key={i}
                className="skeleton-bar"
                style={{
                  height: `${12 + Math.random() * 20}px`,
                  animationDelay: `${i * 0.05}s`,
                }}
              />
            ))}
          </div>
        )}
        <div
          ref={waveformRef}
          style={{
            flexGrow: 1,
            minWidth: 200,
            opacity: showSkeleton ? 0 : 1,
            position: showSkeleton ? "absolute" : "relative",
            pointerEvents: showSkeleton ? "none" : "auto",
          }}
        />
      </div>
      <div style={{ fontSize: 12, minWidth: 40, textAlign: "right" }}>{displayTime}</div>
    </div>
  );
};

export default AudioPlayer;
